import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '@/lib/api';
import { trackEvent } from '@/lib/faro';
import { useAuth } from '@/hooks/useAuth';
import type { Contract } from '@/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TaskSettings } from '@/components/TaskSettings';
import { PaymentSkipSettings } from '@/components/PaymentSkipSettings';

interface FormState {
    title: string;
    funding_source: string;
    allow_adhoc_fees: boolean;
    post_fees_to_feed: boolean;
    enable_wheel: boolean;
    enable_dice: boolean;
    enable_tasks: boolean;
    task_mode: 'non_cumulative' | 'cumulative' | 'unlimited';
    allow_editing_while_in_debt: boolean;
    allow_random_assignment: boolean;
    allow_self_selection: boolean;
    penalty_on_abandon: boolean;
    public_on_profile: boolean;
    allow_payment_skip: boolean;
    max_skips_allowed: string;
    skip_fee_amount: string;
    skip_resets_annually: boolean;
}

export default function ContractEditPage() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [form, setForm] = useState<FormState | null>(null);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});

    const { data: contractData, isLoading } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => api.get<{ data: Contract }>(`/contracts/${id}`),
    });

    const contract = contractData?.data;
    const isDom = user?.id === contract?.dom?.id;

    useEffect(() => {
        if (contract && !form) {
            setForm({
                title: contract.title,
                funding_source: contract.funding_source ?? '',
                allow_adhoc_fees: contract.allow_adhoc_fees,
                post_fees_to_feed: contract.post_fees_to_feed,
                enable_wheel: contract.enable_wheel ?? false,
                enable_dice: contract.enable_dice ?? false,
                enable_tasks: contract.enable_tasks ?? false,
                task_mode: contract.task_mode ?? 'non_cumulative',
                allow_editing_while_in_debt: contract.allow_editing_while_in_debt ?? true,
                allow_random_assignment: contract.allow_random_assignment ?? false,
                allow_self_selection: contract.allow_self_selection ?? true,
                penalty_on_abandon: contract.penalty_on_abandon ?? false,
                public_on_profile: contract.public_on_profile,
                allow_payment_skip: (contract as unknown as Record<string, unknown>).allow_payment_skip as boolean ?? false,
                max_skips_allowed: String((contract as unknown as Record<string, unknown>).max_skips_allowed ?? '2'),
                skip_fee_amount: String((contract as unknown as Record<string, unknown>).skip_fee_amount ?? '0'),
                skip_resets_annually: (contract as unknown as Record<string, unknown>).skip_resets_annually as boolean ?? true,
            });
        }
    }, [contract, form]);

    const mutation = useMutation({
        mutationFn: (data: Partial<FormState>) =>
            api.put<{ data: Contract }>(`/contracts/${id}`, data),
        onSuccess: () => {
            trackEvent('contract_edited', { contract_id: Number(id) });
            queryClient.invalidateQueries({ queryKey: ['contract', id] });
            queryClient.invalidateQueries({ queryKey: ['contracts'] });
            navigate(`/contracts/${id}`);
        },
        onError: (err: Error) => {
            if (err instanceof ApiError && err.errors) {
                setFieldErrors(err.errors);
            }
            setError(err.message);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!form) return;
        setError('');
        setFieldErrors({});
        mutation.mutate({
            ...form,
            max_skips_allowed: parseInt(form.max_skips_allowed),
            skip_fee_amount: parseFloat(form.skip_fee_amount) || 0,
        } as unknown as Partial<FormState>);
    };

    if (isLoading) {
        return <p className="text-sm text-slate-400">Loading...</p>;
    }

    if (!contract || !isDom) {
        return <p className="text-sm text-red-400">Not authorized to edit this contract.</p>;
    }

    if (!form) return null;

    const set = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
        setForm((f) => f ? { ...f, [field]: e.target.value } : f);

    const toggle = (field: keyof FormState) => () =>
        setForm((f) => f ? { ...f, [field]: !f[field] } : f);

    const fieldError = (field: string) =>
        fieldErrors[field]?.[0] ? (
            <p className="mt-1 text-xs text-red-400">{fieldErrors[field][0]}</p>
        ) : null;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-xl font-semibold">Edit Contract</h1>
                <p className="text-sm text-slate-400">Update contract settings. Financial terms are locked after creation.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Details</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <label className="mb-1 block text-sm text-slate-400">Title</label>
                            <Input value={form.title} onChange={set('title')} required />
                            {fieldError('title')}
                        </div>
                        <div>
                            <label className="mb-1 block text-sm text-slate-400">Funding Source</label>
                            <Input value={form.funding_source} onChange={set('funding_source')} placeholder="e.g. Cash App" />
                        </div>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Options</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.allow_adhoc_fees} onChange={toggle('allow_adhoc_fees')} className="rounded" />
                            Allow ad-hoc fees & taxes
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.post_fees_to_feed} onChange={toggle('post_fees_to_feed')} className="rounded" />
                            Post fees & taxes to public feed
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.public_on_profile} onChange={toggle('public_on_profile')} className="rounded" />
                            Show on public profile
                        </label>
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Addons</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.enable_wheel} onChange={toggle('enable_wheel')} className="rounded" />
                            Wheel of Fortune
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.enable_dice} onChange={toggle('enable_dice')} className="rounded" />
                            Dice Roll
                        </label>
                        <label className="flex items-center gap-3 text-sm">
                            <input type="checkbox" checked={form.enable_tasks} onChange={toggle('enable_tasks')} className="rounded" />
                            Tasks
                        </label>
                        {(form.enable_wheel || form.enable_dice) && (
                            <p className="text-xs text-slate-500">Wheel and dice games are configured on the contract detail page.</p>
                        )}
                    </CardContent>
                </Card>

                {form.enable_tasks && (
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base">Task Settings</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <TaskSettings
                                settings={{
                                    task_mode: form.task_mode,
                                    allow_editing_while_in_debt: form.allow_editing_while_in_debt,
                                    allow_random_assignment: form.allow_random_assignment,
                                    allow_self_selection: form.allow_self_selection,
                                    penalty_on_abandon: form.penalty_on_abandon,
                                }}
                                onChange={(s) => setForm(f => f ? { ...f, ...s } : f)}
                            />
                        </CardContent>
                    </Card>
                )}

                <Card>
                    <CardHeader>
                        <CardTitle className="text-base">Payment Skip</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <PaymentSkipSettings
                            allowPaymentSkip={form.allow_payment_skip}
                            maxSkipsAllowed={form.max_skips_allowed}
                            skipFeeAmount={form.skip_fee_amount}
                            skipResetsAnnually={form.skip_resets_annually}
                            onChange={(field, value) => setForm(f => f ? { ...f, [field]: value } : f)}
                        />
                    </CardContent>
                </Card>

                {error && (
                    <div className="rounded-md bg-red-900/30 p-3 text-sm text-red-400">{error}</div>
                )}

                <div className="flex gap-3">
                    <Button type="submit" disabled={mutation.isPending} className="flex-1">
                        {mutation.isPending ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button type="button" variant="outline" onClick={() => navigate(`/contracts/${id}`)}>
                        Cancel
                    </Button>
                </div>
            </form>
        </div>
    );
}
